import { cn } from '@/lib/utils';
import { useContractAbi } from '@/hooks/contract/use-contract-abi';
import type { NetworkType } from '@mls-wallet/shared-types';
import { ContractAbiDetailsType } from '@mls-wallet/shared-types';
import { Loader2 } from 'lucide-react';
import { AddressDisplay } from './address-display';

type ContractNameAddressProps = {
  address: string;
  networkId: NetworkType;
  className?: string;
  details?: React.ReactNode;
  nameOverride?: string | null;
};

/**
 * Displays a contract label above the address.
 *
 * Address-book labels should always win when we have them. If the caller does
 * not provide one, we fall back to the ABI-derived contract display name so
 * read-only policy/proposal views still have a friendly label.
 */
export function ContractNameAddress({
  address,
  networkId,
  className,
  details = null,
  nameOverride = null,
}: ContractNameAddressProps) {
  const preferredName = nameOverride?.trim() || null;
  const contractAbi = useContractAbi(
    preferredName
      ? null
      : {
          address,
          networkType: networkId,
        }
  );

  if (!preferredName && contractAbi.isLoading) {
    return (
      <div className={cn('flex items-center h-10', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const name =
    preferredName ??
    (contractAbi.type === ContractAbiDetailsType.ERROR
      ? null
      : contractAbi.contractDisplayName);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <AddressDisplay
        address={address}
        name={name}
        networkId={networkId}
        showAddressName
        showCopy
        showBlockExplorerLink
      />
      {details ? (
        <div className="text-xs text-muted-foreground">{details}</div>
      ) : null}
    </div>
  );
}
